import { MetricsResponse, Placeholder, UserQuery, BenchmarkingIndexValues } from 'API';
import { StyledTableContainer } from 'components/Main/table/TableStyles';
import { fullSources } from 'customHooks/useDataSources';
import React, { useEffect, useState } from 'react';
import SavedContainer from '../SavedContainer';
import { useDataQuery } from 'customHooks/useDataQuery';
import { useJoinData } from 'customHooks/useJoinData';
import { useVideoParams } from 'customHooks/useParameters';
import { useCampaignContext } from 'context/CampaignContext';

type Props = {
  isGraph: boolean;
  componentID: string;
  publishedQueriesDetails?: Placeholder;
};

const ClientPlaceholderContainer = (props: Props) => {
  const { isGraph, componentID, publishedQueriesDetails } = props;
  const fromTitle = publishedQueriesDetails?.title ?? 'Add title here';
  const [chartMetrics, setChartMetrics] = useState<Array<MetricsResponse>>(
    (publishedQueriesDetails?.metrics as MetricsResponse[]) ?? [],
  );

  // When publishedQueriesDetails changes (e.g. switching BVOD→TV→BVOD causes a remount
  // with undefined details, then remount again with BVOD data), resync chartMetrics so
  // the charts can recompute options on the second BVOD visit.
  useEffect(() => {
    const metrics = publishedQueriesDetails?.metrics as MetricsResponse[] | undefined;
    if (metrics && metrics.length > 0) {
      setChartMetrics(metrics);
    }
  }, [publishedQueriesDetails]);

  const [mainData, mainQueryStatus, mainError, mainQueryID] = useDataQuery({
    query: undefined,
    querySource: 'QUERY',
    publishedOutputLocation: publishedQueriesDetails?.outputlocation,
    publishedQueriesDetails: publishedQueriesDetails,
  });
  const [mainTotalData, mainTotalQueryStatus, , ,] = useDataQuery({
    query: undefined,
    querySource: 'QUERY TOTAL',
    publishedOutputLocation: publishedQueriesDetails?.outputlocationTotal,
    publishedQueriesDetails: publishedQueriesDetails,
  });
  const [ccData, ccQueryStatus, , ccQueryID] = useDataQuery({
    query: undefined,
    querySource: 'QUERY',
    publishedOutputLocation: publishedQueriesDetails?.outputlocation_cc,
    publishedQueriesDetails: publishedQueriesDetails,
  });
  const [ccTotalData, ccTotalQueryStatus, , ,] = useDataQuery({
    query: undefined,
    querySource: 'QUERY TOTAL',
    publishedOutputLocation: publishedQueriesDetails?.outputlocationTotal_cc,
    publishedQueriesDetails: publishedQueriesDetails,
  });
  const [benchmarkData, benchmarkQueryStatus, , benchmarkQueryID] = useDataQuery({
    query: undefined,
    querySource: 'QUERY',
    publishedOutputLocation: publishedQueriesDetails?.outputlocation_benchmark,
    publishedQueriesDetails: publishedQueriesDetails,
  });
  const [benchmarkTotalData, benchmarkTotalQueryStatus, , ,] = useDataQuery({
    query: undefined,
    querySource: 'QUERY TOTAL',
    publishedOutputLocation: publishedQueriesDetails?.outputlocationTotal_benchmark,
    publishedQueriesDetails: publishedQueriesDetails,
  });

  const [joinedData] = useJoinData(
    mainData as any[] | undefined,
    mainTotalData as any[] | undefined,
    ccData as any[] | undefined,
    ccTotalData as any[] | undefined,
    benchmarkData as any[] | undefined,
    benchmarkTotalData as any[] | undefined,
    isGraph,
    mainQueryStatus,
    mainTotalQueryStatus,
    ccQueryStatus,
    ccTotalQueryStatus,
    benchmarkQueryStatus,
    benchmarkTotalQueryStatus,
    chartMetrics,
    setChartMetrics,
    componentID,
    'OLD', // newQuery - default for client components
    false, // defaultSelected - default for client components
    publishedQueriesDetails?.benchmarkingIndex || null, // Read benchmark index from published details
  );
  const [videoSource] = useVideoParams();
  const { dashType } = useCampaignContext();

  return (
    <StyledTableContainer expand={0}>
      <SavedContainer
        joinedData={joinedData && joinedData.every((e: any) => e !== undefined) ? joinedData : undefined}
        isGraph={isGraph} // COMES FROM PARENT
        title={fromTitle} // COMES FROM PARENT
        page={7}
        chartMetrics={chartMetrics} // COMES FROM PARENT
        componentID={componentID} // COMES FROM PARENT
        element={
          dashType === 'TV' && videoSource === 'BVOD'
            ? {
                type: 'Chart',
                minor: 2,
                id: componentID,
              }
            : undefined
        }
      ></SavedContainer>
    </StyledTableContainer>
  );
};

export default ClientPlaceholderContainer;
